import {
  forwardRef,
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';

import { BREAKPOINTS } from '../../hooks/is-mobile';
import { cn } from '@web/lib/shadcn';

interface TableResponsiveProps extends HTMLAttributes<HTMLDivElement> {
  /** Columns to prioritize on smaller screens */
  priorityColumns?: string[];
  /** Use card layout on mobile instead of horizontal scroll */
  cardOnMobile?: boolean;
}

const TableResponsive = forwardRef<HTMLDivElement, TableResponsiveProps>(
  ({ className, priorityColumns, cardOnMobile = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'relative w-full',
          cardOnMobile ? 'overflow-visible' : 'overflow-auto',
          className
        )}
        data-priority-columns={priorityColumns?.join(',')}
        data-card-on-mobile={cardOnMobile}
        {...props}
      />
    );
  }
);
TableResponsive.displayName = 'TableResponsive';

const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    /**
     * We give the parent div a subtle border and rounding to help
     * frame our table in a minimal way.
     */
    <div className="border-border bg-background relative w-full overflow-auto rounded-md border">
      <table
        ref={ref}
        /**
         * Using `border-collapse: collapse` by default for fewer
         * visible "extra" lines. You can switch to `border-separate`
         * and manage `border-spacing` if you prefer.
         */
        className={cn(
          'text-foreground w-full caption-bottom border-collapse text-sm',
          className,
        )}
        aria-label="Content table"
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'border-border bg-muted/50 text-muted-foreground border-b text-xs uppercase tracking-wider',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-border bg-muted/50 text-muted-foreground border-t font-medium',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  /** If provided, the row will be clickable, calling this callback on click. */
  onDetails?: () => void;
  /** For mobile views, allow row to be expanded to show more data */
  expandable?: boolean;
  /** Hidden content shown when expanded on mobile */
  expandedContent?: ReactNode;
};

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, onDetails, onClick, expandable, expandedContent, ...props }, ref) => {
    const [expanded, setExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < BREAKPOINTS.md);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <>
        <tr
          ref={ref}
          onClick={(event) => {
            // If there's also a custom onClick, call that first
            onClick?.(event);
            
            if (expandable && isMobile) {
              // Stop propagation to prevent onDetails from firing
              event.stopPropagation();
              setExpanded(!expanded);
            } else {
              // If expandable is not active, call onDetails
              onDetails?.();
            }
          }}
          className={cn(
            'border-border hover:bg-muted/20 border-b transition-colors',
            '[&[data-state="selected"]]:bg-muted h-12 p-12',
            (onDetails || (expandable && isMobile)) && 'cursor-pointer',
            className,
          )}
          {...props}
        >
          {props.children}
          {expandable && isMobile && (
            <td className="p-2 text-right">
              <button 
                onClick={(e) => {
                  e.stopPropagation(); 
                  setExpanded(!expanded);
                }}
                className="rounded-full p-1 hover:bg-muted-foreground/10"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            </td>
          )}
        </tr>
        {expandable && isMobile && expanded && expandedContent && (
          <tr className="bg-muted/10">
            <td colSpan={100} className="p-3 px-4">
              <div className="text-sm">{expandedContent}</div>
            </td>
          </tr>
        )}
      </>
    );
  },
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Mark column as high priority for mobile */
  priority?: boolean;
  /** Hide this column on mobile */
  hiddenOnMobile?: boolean;
}

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, priority, hiddenOnMobile, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'px-4 py-3 text-left font-semibold',
        '[&:has([role=checkbox])]:pr-0',
        hiddenOnMobile && 'hidden md:table-cell',
        priority && 'table-cell',
        className,
      )}
      data-priority={priority ? "true" : "false"}
      {...props}
    />
  )
);
TableHead.displayName = 'TableHead';

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Mark cell as high priority for mobile */
  priority?: boolean;
  /** Hide this cell on mobile */
  hiddenOnMobile?: boolean;
  /** Label for this cell when displayed in card view (mobile) */
  mobileLabel?: string;
}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, priority, hiddenOnMobile, mobileLabel, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'px-2 py-1 text-left align-middle [&:has([role=checkbox])]:pr-0',
        hiddenOnMobile && 'hidden md:table-cell',
        priority && 'table-cell',
        mobileLabel && 'md:before:hidden before:content-[attr(data-label)] before:font-medium before:text-muted-foreground before:block md:before:hidden sm:before:w-24 sm:before:inline-block',
        className,
      )}
      data-priority={priority ? "true" : "false"}
      data-label={mobileLabel}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

/** For mobile card view, adds a key-value display */
const TableCardCell = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    label: string;
  }
>(({ className, label, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('py-2 flex justify-between', className)}
    {...props}
  >
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className="text-sm">{children}</span>
  </div>
));
TableCardCell.displayName = 'TableCardCell';

/** Container for card-style mobile views */
const TableCardView = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'md:hidden space-y-4',
      className
    )}
    {...props}
  />
));
TableCardView.displayName = 'TableCardView';

/** Wrapper for horizontal scrolling tables on mobile */
const TableScrollWrapper = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    /** Apply a min-width to ensure horizontal scrolling */
    minWidth?: string;
  }
>(({ className, minWidth = '640px', children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'w-full overflow-x-auto pb-2',
      className
    )}
    {...props}
  >
    <div style={{ minWidth }}>
      {children}
    </div>
  </div>
));
TableScrollWrapper.displayName = 'TableScrollWrapper';

/** Individual card for mobile view */
const TableCard = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'bg-card p-4 rounded-lg border shadow-sm',
      className
    )}
    {...props}
  />
));
TableCard.displayName = 'TableCard';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableResponsive,
  TableCardView,
  TableCard,
  TableCardCell,
  TableScrollWrapper,
};